// A drum loop's swing, measured from its own audio (docs/swing-plan.md §2.1, §4.1).
//
// The kit is the band's clock. A swing kit is a recording, and its ride's skip
// note sits wherever that drummer put it: 0.78 of the beat on a slow Philly Joe
// Jones loop, 0.59 on a fast Brian Blade one. A band that swings every "and" at
// the triplet's 0.667 misses those skip notes by 10 to 86 ms, and that alone
// keeps the bass and the melody from locking with the drums. So each loop is
// measured once, and a band that swings with the kit puts its "and"s there.
//
// Method (the research script's, ported): a high-band (2.5–11 kHz: ride,
// hi-hat, stick) onset-strength envelope, log-magnitude spectral flux over a
// 512-sample window at ~22 kHz, folded modulo the beat into 96 bins. The
// strongest bin near the beat is the loop's "one"; the strongest bin between
// 0.5 and 0.85 of the beat after it is the skip note. The value is kept only
// when the evidence agrees: the ride band (3–8 kHz) finds the same bin, the
// local centroid of the energy around the peak sits on it, and the peak stands
// out from the histogram's floor, and nothing sounds a sixteenth after the
// beat (a sixteenth-note groove's 0.75 is the "a" of a straight grid, not a
// swung "and"). A loop that plays straight, a ride on quarters, a sixteenth
// groove or a skip note too faint to place reads as null, and the band then
// swings the triplet as it always did.
//
// Pure apart from the small cache at the bottom, which remembers a loop the
// player measured at decode (an uploaded loop has no manifest value).

export interface LoopSwingMeasure {
  /** Where the "and" sounds, as a fraction of the beat, or null when unclear. */
  swing: number | null;
  /** The strongest offbeat bin in the high band, a fraction of the beat. */
  peak: number;
  /** The same in the ride's own band. */
  ridePeak: number;
  /** The centroid of the energy within 0.05 of the peak. */
  centroid: number;
  /** The peak's height over the histogram's median. */
  prominence: number;
  /** The strongest bin a sixteenth after the beat (0.2–0.3), over the median. */
  sixteenthProminence: number;
  /** Where the loop's own "one" sits against the nominal grid, beats (negative = early). */
  oneOffsetBeats: number;
}

const TARGET_RATE = 22050;
const WIN = 512;
const HOP = 64;
const BINS = 96;
const HIGH_BAND: [number, number] = [2500, 11025];
const RIDE_BAND: [number, number] = [3000, 8000];

/** The thresholds a measurement must clear to count (see the header). */
const SWUNG_FROM = 0.56;
const BANDS_AGREE = 0.03;
const CENTROID_AGREES = 0.02;
const MIN_PROMINENCE = 1.05;
/** A sixteenth after the beat this loud means a sixteenth-note groove, whose
 *  0.75 peak is the "a" of a straight grid, not a swung "and". */
const SIXTEENTH_GROOVE = 1.3;

/** In-place radix-2 complex FFT over `re`/`im` (length a power of two). */
function fft(re: Float64Array, im: Float64Array, cos: Float64Array, sin: Float64Array): void {
  const n = re.length;
  for (let i = 1, j = 0; i < n; i++) {
    let bit = n >> 1;
    for (; j & bit; bit >>= 1) j ^= bit;
    j ^= bit;
    if (i < j) {
      const tr = re[i];
      re[i] = re[j];
      re[j] = tr;
      const ti = im[i];
      im[i] = im[j];
      im[j] = ti;
    }
  }
  for (let size = 2; size <= n; size <<= 1) {
    const half = size >> 1;
    const step = n / size;
    for (let start = 0; start < n; start += size) {
      for (let k = 0; k < half; k++) {
        const wr = cos[k * step];
        const wi = -sin[k * step];
        const a = start + k;
        const b = a + half;
        const xr = re[b] * wr - im[b] * wi;
        const xi = re[b] * wi + im[b] * wr;
        re[b] = re[a] - xr;
        im[b] = im[a] - xi;
        re[a] += xr;
        im[a] += xi;
      }
    }
  }
}

/** Mono samples near 22 kHz: whole-number decimation, averaging each group. */
function decimate(samples: Float32Array, sampleRate: number): { x: Float32Array; rate: number } {
  const factor = Math.max(1, Math.round(sampleRate / TARGET_RATE));
  if (factor === 1) return { x: samples, rate: sampleRate };
  const out = new Float32Array(Math.floor(samples.length / factor));
  for (let i = 0; i < out.length; i++) {
    let sum = 0;
    for (let k = 0; k < factor; k++) sum += samples[i * factor + k];
    out[i] = sum / factor;
  }
  return { x: out, rate: sampleRate / factor };
}

/** Two onset envelopes (high band, ride band) and their frame times, seconds. */
function onsetEnvelopes(
  x: Float32Array,
  rate: number,
): { high: Float64Array; ride: Float64Array; times: Float64Array } {
  const frames = x.length >= WIN ? Math.floor((x.length - WIN) / HOP) + 1 : 0;
  const bins = WIN / 2 + 1;
  const hann = new Float64Array(WIN);
  const cos = new Float64Array(WIN);
  const sin = new Float64Array(WIN);
  for (let i = 0; i < WIN; i++) {
    hann[i] = 0.5 - 0.5 * Math.cos((2 * Math.PI * i) / (WIN - 1));
    cos[i] = Math.cos((2 * Math.PI * i) / WIN);
    sin[i] = Math.sin((2 * Math.PI * i) / WIN);
  }
  const binHz = rate / WIN;
  const inHigh = (k: number) => k * binHz >= HIGH_BAND[0] && k * binHz < HIGH_BAND[1];
  const inRide = (k: number) => k * binHz >= RIDE_BAND[0] && k * binHz < RIDE_BAND[1];
  const re = new Float64Array(WIN);
  const im = new Float64Array(WIN);
  let prev = new Float64Array(bins);
  let curr = new Float64Array(bins);
  const count = Math.max(0, frames - 1);
  const high = new Float64Array(count);
  const ride = new Float64Array(count);
  const times = new Float64Array(count);
  for (let f = 0; f < frames; f++) {
    const offset = f * HOP;
    for (let i = 0; i < WIN; i++) {
      re[i] = x[offset + i] * hann[i];
      im[i] = 0;
    }
    fft(re, im, cos, sin);
    for (let k = 0; k < bins; k++) curr[k] = Math.log1p(1000 * Math.hypot(re[k], im[k]));
    if (f > 0) {
      let h = 0;
      let r = 0;
      for (let k = 0; k < bins; k++) {
        const rise = curr[k] - prev[k];
        if (rise <= 0) continue;
        if (inHigh(k)) h += rise;
        if (inRide(k)) r += rise;
      }
      high[f - 1] = h;
      ride[f - 1] = r;
      // A frame's time is its window's CENTRE, so an attack is not read half
      // a window early.
      times[f - 1] = (f * HOP + WIN / 2) / rate;
    }
    const swap = prev;
    prev = curr;
    curr = swap;
  }
  return { high, ride, times };
}

/** The envelope folded modulo the beat: 96 bins, lightly smoothed, summing to 1. */
function fold(envelope: Float64Array, times: Float64Array, period: number): Float64Array {
  const raw = new Float64Array(BINS);
  for (let i = 0; i < envelope.length; i++) {
    const phase = (times[i] % period) / period;
    raw[Math.floor(phase * BINS) % BINS] += envelope[i];
  }
  const out = new Float64Array(BINS);
  let total = 0;
  for (let i = 0; i < BINS; i++) {
    const value = 0.25 * raw[(i + BINS - 1) % BINS] + 0.5 * raw[i] + 0.25 * raw[(i + 1) % BINS];
    out[i] = value;
    total += value;
  }
  if (total > 0) for (let i = 0; i < BINS; i++) out[i] /= total;
  return out;
}

function median(values: Float64Array): number {
  const sorted = Array.from(values).sort((a, b) => a - b);
  const mid = sorted.length >> 1;
  return sorted.length % 2 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2;
}

/** The strongest bin whose position after the "one" lies in [from, to]. */
function peakIn(h: Float64Array, one: number, from: number, to: number): number {
  let best = -1;
  for (let i = 0; i < BINS; i++) {
    const rel = ((i - one + BINS) % BINS) / BINS;
    if (rel < from - 1e-9 || rel > to + 1e-9) continue;
    if (best < 0 || h[i] > h[best]) best = i;
  }
  return best;
}

const round3 = (value: number) => Math.round(value * 1000) / 1000;

/**
 * Measure a loop: mono `samples` at `sampleRate`, played at `bpm` (the
 * manifest's tempo, which divides the file into whole beats).
 */
export function measureLoopSwing(
  samples: Float32Array,
  sampleRate: number,
  bpm: number,
): LoopSwingMeasure {
  const { x, rate } = decimate(samples, sampleRate);
  const { high, ride, times } = onsetEnvelopes(x, rate);
  const period = 60 / bpm;
  const h = fold(high, times, period);
  const r = fold(ride, times, period);
  // The loop's own "one": the strongest bin within 15% of the beat.
  let one = 0;
  for (let i = 0; i < BINS; i++) {
    if (i > BINS * 0.15 && i < BINS * 0.85) continue;
    if (h[i] > h[one]) one = i;
  }
  const rel = (i: number) => ((i - one + BINS) % BINS) / BINS;
  const peakBin = peakIn(h, one, 0.5, 0.85);
  const rideBin = peakIn(r, one, 0.5, 0.85);
  const floor = median(h);
  let weight = 0;
  let moment = 0;
  for (let i = 0; i < BINS; i++) {
    const position = rel(i);
    if (Math.abs(position - rel(peakBin)) > 0.05 + 1e-9) continue;
    const excess = Math.max(0, h[i] - floor);
    weight += excess;
    moment += excess * position;
  }
  const peak = peakBin < 0 ? 0.5 : rel(peakBin);
  const ridePeak = rideBin < 0 ? 0.5 : rel(rideBin);
  const centroid = weight > 0 ? moment / weight : peak;
  // Height over the floor; a clean recording with silence between its strokes
  // has a floor of 0, where anything at all stands out.
  const over = (bin: number) =>
    bin < 0 || h[bin] <= 0 ? 0 : floor > 0 ? h[bin] / floor : Number.POSITIVE_INFINITY;
  const prominence = over(peakBin);
  const sixteenthProminence = over(peakIn(h, one, 0.2, 0.3));
  const clear =
    peak >= SWUNG_FROM &&
    centroid >= SWUNG_FROM &&
    Math.abs(peak - ridePeak) <= BANDS_AGREE + 1e-9 &&
    Math.abs(peak - centroid) <= CENTROID_AGREES + 1e-9 &&
    prominence >= MIN_PROMINENCE &&
    sixteenthProminence < SIXTEENTH_GROOVE;
  return {
    swing: clear ? round3(centroid) : null,
    peak: round3(peak),
    ridePeak: round3(ridePeak),
    centroid: round3(centroid),
    prominence: Number.isFinite(prominence) ? Math.round(prominence * 100) / 100 : prominence,
    sixteenthProminence: Number.isFinite(sixteenthProminence)
      ? Math.round(sixteenthProminence * 100) / 100
      : sixteenthProminence,
    oneOffsetBeats: round3((one <= BINS / 2 ? one : one - BINS) / BINS),
  };
}

/** Average the channels of a decoded buffer (an AudioBuffer or a stand-in). */
export function monoSamples(buffer: {
  numberOfChannels: number;
  length: number;
  getChannelData(channel: number): Float32Array;
}): Float32Array {
  if (buffer.numberOfChannels <= 1) return buffer.getChannelData(0);
  const out = new Float32Array(buffer.length);
  for (let c = 0; c < buffer.numberOfChannels; c++) {
    const data = buffer.getChannelData(c);
    for (let i = 0; i < out.length; i++) out[i] += data[i] / buffer.numberOfChannels;
  }
  return out;
}

// --- loops measured as they are decoded ------------------------------------

const measured = new Map<string, number | null>();
const listeners = new Set<() => void>();

/** Remember a loop's measured swing by its audio's address. */
export function rememberLoopSwing(url: string, swing: number | null): void {
  if (measured.has(url) && measured.get(url) === swing) return;
  measured.set(url, swing);
  for (const listener of listeners) listener();
}

/** A measurement made at decode: undefined until the loop has been decoded. */
export function measuredLoopSwing(url: string): number | null | undefined {
  return measured.get(url);
}

export function subscribeLoopSwing(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** For tests: forget every loop measured at decode. */
export function forgetMeasuredLoopSwings(): void {
  measured.clear();
}

/**
 * A loop's swing: the manifest's measurement when it has one (null included,
 * which means measured and unclear), else the one made at decode.
 */
export function loopSwingOf(
  loop: { swing?: number | null },
  url: string,
): number | null | undefined {
  return loop.swing !== undefined ? loop.swing : measuredLoopSwing(url);
}
